import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import DataTable from './components/DataTable';
import FilterBar from './components/FilterBar';
import UploadModal from './components/UploadModal';
import SendEmailModal from './components/SendEmailModal';
import EmailSettings from './pages/EmailSettings';
import EmailTemplates from './pages/EmailTemplates';
import Campaigns from './pages/Campaigns';
import {
  FiDatabase, FiSettings, FiMail, FiUpload, FiSend, FiTrash2,
  FiDownload, FiCheckSquare, FiChevronLeft, FiChevronRight,
  FiCheckCircle, FiAlertCircle, FiInfo, FiX, FiMenu, FiBarChart2,
  FiLayers, FiActivity
} from 'react-icons/fi';

// ─── Toast System ─────────────────────────────────────────────────────────────
let toastId = 0;

function ToastContainer({ toasts, removeToast }) {
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span className="toast-icon">
            {t.type === 'success' && <FiCheckCircle size={16} />}
            {t.type === 'error' && <FiAlertCircle size={16} />}
            {t.type === 'info' && <FiInfo size={16} />}
          </span>
          <span className="toast-msg">{t.message}</span>
          <button className="toast-close" onClick={() => removeToast(t.id)}>
            <FiX size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  const [currentPage, setCurrentPage] = useState('database');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [filters, setFilters] = useState({ search: '', location: '' });
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, total: 0 });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('');

  const { toasts, addToast, removeToast } = useToast();

  // Fetch datasets list
  const fetchDatasets = useCallback(async () => {
    try {
      const response = await axios.get('/api/companies/datasets');
      setDatasets(response.data.data);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
    }
  }, []);

  // Fetch companies
  const fetchCompanies = useCallback(async (filterParams = {}, sortParams = {}, pageOffset = null) => {
    try {
      setLoading(true);
      const offset = pageOffset !== null ? pageOffset : pagination.offset;
      const params = {
        limit: pagination.limit,
        offset,
        sortBy: sortParams.sortBy || sortBy,
        sortOrder: sortParams.sortOrder || sortOrder,
        ...filterParams
      };
      const response = await axios.get('/api/companies', { params });
      setCompanies(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total }));
      if (sortParams.sortBy) setSortBy(sortParams.sortBy);
      if (sortParams.sortOrder) setSortOrder(sortParams.sortOrder);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      addToast('Failed to fetch company data', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.offset, pagination.limit, sortBy, sortOrder, addToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDatasets();
    fetchCompanies({ ...filters, source_file: selectedDataset }, {}, pagination.offset);
  }, [pagination.offset, selectedDataset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchCompanies({ ...newFilters, source_file: selectedDataset, offset: 0 }, {}, 0);
  };

  const handleDatasetChange = (value) => {
    setSelectedDataset(value);
    setSelectedRows(new Set());
    setPagination(prev => ({ ...prev, offset: 0 }));
    // useEffect re-runs on selectedDataset change; if offset was already 0, force-fetch now
    fetchCompanies({ ...filters, source_file: value }, {}, 0);
  };

  const handleDeleteDataset = async () => {
    if (!selectedDataset) return;
    const dataset = datasets.find(d => d.source_file === selectedDataset);
    const count = dataset ? dataset.count : 'all';
    if (!window.confirm(`Permanently delete all ${count} companies from "${selectedDataset}"? This cannot be undone.`)) return;

    try {
      setLoading(true);
      await axios.delete(`/api/companies/dataset/${encodeURIComponent(selectedDataset)}`);
      setSelectedDataset('');
      setSelectedRows(new Set());
      await fetchDatasets();
      fetchCompanies({ ...filters, source_file: '' }, {}, 0);
      addToast(`Deleted dataset "${selectedDataset}"`, 'success');
    } catch (error) {
      addToast('Failed to delete dataset', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    fetchCompanies({ ...filters, source_file: selectedDataset }, { sortBy: column, sortOrder: newOrder });
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/companies/filtered-ids', {
        params: { ...filters, source_file: selectedDataset, sortBy, sortOrder }
      });
      const allIds = response.data.ids;
      setSelectedRows(selectedRows.size === allIds.length ? new Set() : new Set(allIds));
    } catch (error) {
      addToast('Failed to select records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) {
      addToast('Select companies to delete', 'info');
      return;
    }
    if (!window.confirm(`Permanently delete ${selectedRows.size} companies? This cannot be undone.`)) return;

    try {
      setLoading(true);
      await axios.post('/api/companies/bulk-delete', { ids: Array.from(selectedRows) });
      setSelectedRows(new Set());
      fetchCompanies({ ...filters, source_file: selectedDataset });
      addToast(`Deleted ${selectedRows.size} companies`, 'success');
    } catch (error) {
      addToast('Failed to delete companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (selectedRows.size === 0) {
      addToast('Select companies to export', 'info');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(
        '/api/companies/export',
        { ids: Array.from(selectedRows), format },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `companies-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      addToast(`Exported ${selectedRows.size} companies as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      addToast('Failed to export', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchDatasets();
    fetchCompanies({ ...filters, source_file: selectedDataset }, {}, 0);
    addToast('Excel file imported successfully', 'success');
  };

  const handleSendSuccess = () => {
    addToast('Email campaign sent successfully', 'success');
  };

  const navItems = [
    { id: 'database', label: 'Database', icon: FiDatabase },
    { id: 'campaigns', label: 'Campaigns', icon: FiActivity },
    { id: 'email-settings', label: 'Email Settings', icon: FiSettings },
    { id: 'email-templates', label: 'Email Templates', icon: FiMail },
  ];

  const currentPage_offset = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <FiBarChart2 size={22} />
          <span>OutreachDB</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`sidebar-link ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => {
                setCurrentPage(item.id);
                setSidebarOpen(false);
              }}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span>v1.0.0</span>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main area */}
      <div className="main-area">
        {/* Top bar */}
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
            <FiMenu size={20} />
          </button>
          <div className="topbar-title">
            {navItems.find(n => n.id === currentPage)?.label}
          </div>
          {currentPage === 'database' && selectedRows.size > 0 && (
            <div className="topbar-selection">
              <span>{selectedRows.size} selected</span>
            </div>
          )}
        </header>

        {/* Page content */}
        <main className="page-content">
          {currentPage === 'database' && (
            <div className="db-page">
              {/* Action bar */}
              <div className="action-bar">
                <div className="action-bar-left">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <FiUpload size={15} />
                    Import Excel
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={handleSelectAll}
                    disabled={loading || companies.length === 0}
                  >
                    <FiCheckSquare size={15} />
                    Select All
                  </button>
                  {selectedDataset && (
                    <button
                      className="btn btn-danger"
                      onClick={handleDeleteDataset}
                      disabled={loading}
                      title={`Delete all records from "${selectedDataset}"`}
                    >
                      <FiLayers size={15} />
                      Delete Dataset
                    </button>
                  )}
                </div>

                {selectedRows.size > 0 && (
                  <div className="action-bar-right">
                    <button
                      className="btn btn-success"
                      onClick={() => setShowSendModal(true)}
                      disabled={loading}
                    >
                      <FiSend size={15} />
                      Send Email ({selectedRows.size})
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleExport('xlsx')}
                      disabled={loading}
                    >
                      <FiDownload size={15} />
                      Excel
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleExport('csv')}
                      disabled={loading}
                    >
                      <FiDownload size={15} />
                      CSV
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={handleBulkDelete}
                      disabled={loading}
                    >
                      <FiTrash2 size={15} />
                      Delete ({selectedRows.size})
                    </button>
                  </div>
                )}
              </div>

              {/* Filters */}
              <FilterBar
                onFilterChange={handleFilterChange}
                datasets={datasets}
                selectedDataset={selectedDataset}
                onDatasetChange={handleDatasetChange}
              />

              {/* Stats row */}
              <div className="stats-row">
                <div className="stat-chip">
                  <span className="stat-chip-label">Total Records</span>
                  <span className="stat-chip-value">{pagination.total.toLocaleString()}</span>
                </div>
                {selectedRows.size > 0 && (
                  <div className="stat-chip selected">
                    <span className="stat-chip-label">Selected</span>
                    <span className="stat-chip-value">{selectedRows.size.toLocaleString()}</span>
                  </div>
                )}
                {loading && <span className="loading-text">Loading...</span>}
              </div>

              {/* Table */}
              <DataTable
                companies={companies}
                selectedRows={selectedRows}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                selectAllChecked={selectedRows.size === companies.length && companies.length > 0}
                onSort={handleSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    disabled={pagination.offset === 0}
                    onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                  >
                    <FiChevronLeft size={16} />
                    Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage_offset} of {totalPages}
                  </span>
                  <button
                    className="page-btn"
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                  >
                    Next
                    <FiChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {currentPage === 'campaigns' && <Campaigns addToast={addToast} />}
          {currentPage === 'email-settings' && <EmailSettings addToast={addToast} />}
          {currentPage === 'email-templates' && <EmailTemplates addToast={addToast} />}
        </main>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showSendModal && (
        <SendEmailModal
          selectedCount={selectedRows.size}
          selectedIds={Array.from(selectedRows)}
          onClose={() => setShowSendModal(false)}
          onSuccess={handleSendSuccess}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;
